'use client';

import {useState, useEffect, useRef} from 'react';
import {useTranslations, useLocale} from 'next-intl';
import {usePathname} from 'next/navigation';
import MobileMenu from './MobileMenu';
import SearchBar from './SearchBar';
import TunisianFlag from '../ui/TunisianFlag';

const navKeysTop = ['contact', 'about', 'news', 'programs'] as const;
const navKeysAfterProjects = ['testimonials', 'videos', 'surveys', 'partners'] as const;

const projectsDropdown = [
  {key: 'projects', path: '/projets'},
  {key: 'structures', path: '/structures'},
  {key: 'accomplished', path: '/projets-accomplis'},
] as const;

const navPaths: Record<string, string> = {
  contact: '/contact',
  about: '/a-propos',
  news: '/actualites',
  programs: '/programmes',
  projects: '/projets',
  accomplished: '/projets-accomplis',
  vitrines: '/vitrines',
  testimonials: '/temoignages',
  structures: '/structures',
  surveys: '/sondages',
  partners: '/partenaires',
  videos: '/videos',
};

const localeLabels: Record<string, string> = {
  fr: 'FR',
  ar: 'عربي',
  en: 'EN',
};

export default function Header() {
  const t = useTranslations('nav');
  const tc = useTranslations('common');
  const locale = useLocale();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loggedUser, setLoggedUser] = useState<{nom: string; role: string} | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('utss_user');
    if (stored) setLoggedUser(JSON.parse(stored));
  }, []);

  const switchLocale = (newLocale: string) => {
    const pathWithoutLocale = pathname.replace(/^\/(fr|ar|en)/, '') || '/';
    window.location.href = `/${newLocale}${pathWithoutLocale}`;
  };

  return (
    <header role="banner" className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Main header — full width */}
      <div className="w-full px-4 lg:px-6">
        <div className="flex items-center h-20">
          {/* Logo */}
          <a href={`/${locale}`} className="flex items-center gap-3 shrink-0 mr-4">
            <img src="/images/utss/rose.svg" alt="UTSS" className="h-14 w-auto" />
            <div className="leading-tight">
              <span className="text-primary font-bold text-2xl block">UTSS</span>
              <span className="hidden md:block text-muted text-sm">
                {tc('solidarity')}
              </span>
            </div>
            <TunisianFlag className="ms-1" />
          </a>

          {/* Nav links — fill remaining space */}
          <nav className="hidden lg:flex items-center flex-1 gap-0.5 xl:gap-1" aria-label="Navigation principale">
            {navKeysTop.map((key) => {
              const href = `/${locale}${navPaths[key]}`;
              const isActive = pathname === href || pathname.startsWith(href);
              return (
                <a
                  key={key}
                  href={href}
                  className={`px-2 py-1 text-[13px] xl:text-[14px] font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-foreground/60 hover:text-primary'
                  }`}
                >
                  {t(key)}
                </a>
              );
            })}

            {/* Projets dropdown */}
            <div className="relative group">
              <button
                className={`px-2 py-1 text-[13px] xl:text-[14px] font-medium whitespace-nowrap inline-flex items-center gap-1 transition-colors cursor-pointer ${
                  projectsDropdown.some(p => {
                    const h = `/${locale}${p.path}`;
                    return pathname === h || pathname.startsWith(h);
                  })
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-foreground/60 hover:text-primary'
                }`}
              >
                {t('projects_menu')}
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div className="absolute left-0 top-full mt-0 w-52 bg-white rounded-lg shadow-lg border border-gray-100 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                {projectsDropdown.map(({key, path}) => {
                  const href = `/${locale}${path}`;
                  const isActive = pathname === href || pathname.startsWith(href);
                  return (
                    <a
                      key={key}
                      href={href}
                      className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? 'text-primary bg-primary/5'
                          : 'text-foreground/70 hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      {t(key + '_short')}
                    </a>
                  );
                })}
              </div>
            </div>

            {navKeysAfterProjects.map((key) => {
              const href = `/${locale}${navPaths[key]}`;
              const isActive = pathname === href || pathname.startsWith(href);
              return (
                <a
                  key={key}
                  href={href}
                  className={`px-2 py-1 text-[13px] xl:text-[14px] font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-foreground/60 hover:text-primary'
                  }`}
                >
                  {t(key)}
                </a>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 shrink-0 ml-2">
            {/* Recherche globale */}
            <div className="hidden md:block">
              <SearchBar />
            </div>

            {/* Language dropdown */}
            <div className="relative group hidden sm:block">
              <button className="px-2 py-1 text-sm font-medium text-foreground/60 hover:text-primary inline-flex items-center gap-1 cursor-pointer">
                {localeLabels[locale] || 'FR'}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div className="absolute right-0 top-full mt-0 w-28 bg-white rounded-lg shadow-lg border border-gray-100 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                {(['fr', 'ar', 'en'] as const).filter(l => l !== locale).map(l => (
                  <button
                    key={l}
                    onClick={() => switchLocale(l)}
                    className="block w-full text-left px-4 py-2 text-sm font-medium text-foreground/70 hover:bg-primary/5 hover:text-primary cursor-pointer"
                  >
                    {localeLabels[l]}
                  </button>
                ))}
              </div>
            </div>

            {/* Auth */}
            {loggedUser ? (
              <div className="hidden sm:flex items-center gap-3">
                <a
                  href={`/${locale}/${loggedUser.role === 'donateur' ? 'espace-donateur' : 'dashboard'}`}
                  style={{color: '#1B5E20', textDecorationColor: 'transparent'}}
                  className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  {loggedUser.nom}
                </a>
                <button
                  onClick={() => { localStorage.removeItem('utss_token'); localStorage.removeItem('utss_user'); window.location.href = `/${locale}/auth`; }}
                  className="text-sm text-muted hover:text-secondary transition-colors cursor-pointer"
                >
                  {tc('logout')}
                </button>
              </div>
            ) : (
              <a
                href={`/${locale}/auth`}
                style={{color: '#1B5E20', textDecorationColor: 'transparent'}}
                className="hidden sm:inline-flex items-center gap-2 border border-primary text-base font-medium px-4 py-2 rounded whitespace-nowrap hover:bg-primary/5 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {tc('login')}
              </a>
            )}
            <a
              href={`/${locale}/don`}
              style={{backgroundColor: '#C62828', color: '#ffffff'}}
              className="hidden sm:inline-flex items-center gap-2 hover:opacity-90 text-base font-semibold px-5 py-2 rounded whitespace-nowrap transition-opacity"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {t('donate')}
            </a>
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded hover:bg-surface min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setMobileOpen(true)}
              aria-label="Menu"
            >
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
